import { queryOptions, mutationOptions } from '@tanstack/react-query';
import type { Stored } from './stored';
import { apiClient } from './client';

import type { components } from './generated/hsBookingApi';

type Schemas = components['schemas'];

/* The resources follow the property descriptors of the item type, of
   whatever kind they are, and a patch clears an entry with null; the
   API definition describes only two kinds of numeric resources (see
   doc/TODO.md, section 7). */
type Resources = Record<string, unknown>;

export type BookingItemInsert = Omit<
  Schemas['HsBookingItemInsert'],
  'resources'
> & {
  resources: Resources;
};

export type BookingItemPatch = Omit<
  Schemas['HsBookingItemPatch'],
  'resources'
> & {
  resources?: Resources;
};

export const bookingKeys = {
  all: ['booking'] as const,
  projects: (debitorUuid?: string | null) => [
    ...bookingKeys.all,
    'projects',
    debitorUuid ?? 'all',
  ],
  allItems: () => [...bookingKeys.all, 'items'],
  items: (projectUuid: string | null | undefined) => [
    ...bookingKeys.allItems(),
    projectUuid,
  ],
};

/*
 * Booking projects ("scope") are visible without an assumed role;
 * everything below a project requires the Hostsharing-Assumed-Roles
 * header (see the assume barrier in the project area).
 */
export const bookingQueries = {
  projects: () =>
    queryOptions({
      queryKey: bookingKeys.projects(),
      queryFn: async () => {
        const { data } =
          await apiClient.get<Stored<Schemas['HsBookingProject']>[]>(
            '/booking/projects'
          );
        return data;
      },
    }),
  projectsByDebitor: (debitorUuid: string | null | undefined) =>
    queryOptions({
      queryKey: bookingKeys.projects(debitorUuid),
      queryFn: async () => {
        const { data } = await apiClient.get<
          Stored<Schemas['HsBookingProject']>[]
        >(`/booking/projects?debitorUuid=${debitorUuid}`);
        return data;
      },
      enabled: !!debitorUuid,
    }),
  /*
   * Requires an assumed project role; the Hostsharing-Assumed-Roles
   * header is attached by the interceptor in api/assumedRoles.ts.
   */
  items: (projectUuid: string | null | undefined) =>
    queryOptions({
      queryKey: bookingKeys.items(projectUuid),
      queryFn: async () => {
        const { data } = await apiClient.get<
          Stored<Schemas['HsBookingItem'], 'resources'>[]
        >(`/booking/items?projectUuid=${projectUuid}`);
        return data;
      },
      enabled: !!projectUuid,
    }),
};

export const bookingMutations = {
  /* a resource sent as null clears the entry, one left out keeps its
     value — the semantics of the backend's patcher */
  patchItem: (bookingItemUuid: string | null | undefined) =>
    mutationOptions({
      mutationFn: async (bookingItemPatch: BookingItemPatch) => {
        const { data } = await apiClient.patch<
          Stored<Schemas['HsBookingItem'], 'resources'>
        >(`/booking/items/${bookingItemUuid}`, bookingItemPatch);
        return data;
      },
    }),
  postItem: () =>
    mutationOptions({
      mutationFn: async (bookingItemInsert: BookingItemInsert) => {
        const { data } = await apiClient.post<
          Stored<Schemas['HsBookingItem'], 'resources'>
        >('/booking/items', bookingItemInsert);
        return data;
      },
    }),
};
